// Aba "Com problemas" em /notas-fiscais. Lista unificada de 3 categorias:
//   - rejeitada       (NF SEFAZ devolveu cStat != 100)
//   - erro_comunicacao (NF sem retorno SEFAZ — cron reconcilia a cada 5min)
//   - venda_sem_nf    (venda finalizada com tipo_documento mas sem NF autorizada)
//
// Cada linha tem botoes pra resolver o problema sem sair da tela:
//   - "Emitir" — re-emite via POST /api/{nfce|nfe}/emitir/:orcId
//   - "SEFAZ" (so quando ja existe chave) — consultar status real
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw, FileText, Receipt, Shield, EyeOff, Ban, Info, X, Printer } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { formatMoeda, formatDataHoraSP } from '@fiodecobre/shared';
import { ModalComprovanteVenda, type ComprovanteVendaInfo } from '../../components/notas-fiscais/ModalComprovanteVenda';

// Cooldown do botao Emitir (DA-290): enquanto a ultima tentativa for um
// erro_comunicacao recente (<10min), o sistema ja esta reconciliando sozinho —
// desabilita o Emitir pra nao queimar numero por reclique. O backend reforca com 409.
const COOLDOWN_EMISSAO_MIN = 10;
function minutosDesde(iso: string): number {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return Infinity; // parse falhou → nao trava (fail-open; o 409 reforca)
  return (Date.now() - t) / 60000;
}

interface ProblemaRow {
  categoria: 'rejeitada' | 'erro_comunicacao' | 'venda_sem_nf';
  tipo: 'nfce' | 'nfe' | null;
  nota_id: number | null;
  numero: number | null;
  serie: string | null;
  chave: string | null;
  status: string | null;
  motivo: string | null;
  codigo_status: string | null;
  venda_id: number | null;
  venda_numero: string | null;
  orcamento_id: number;
  orcamento_numero: string | null;
  cliente_id: number | null;
  cliente_nome: string | null;
  vendedor_nome: string | null;
  produtos: string | null;
  valor_total: string | null;
  ocorrido_em: string;
  tipo_documento_esperado: 'nfce' | 'nfe' | null;
  causas?: string[];
  causas_conferidas?: boolean;
}

// Provavel causa da recusa, lida pelo servidor no cadastro ATUAL dos produtos (codigo de
// barras e NCM — as mesmas regras que a emissao confere antes de enviar). Some sozinha
// quando o cadastro e corrigido: e o sinal de que o Emitir vai passar. Cores FIXAS
// (#fde68a + #000), nunca do tema — selo com variavel de tema perde contraste.
function ProvavelCausa({ row }: { row: ProblemaRow }) {
  const causas = row.causas ?? [];
  if (causas.length > 0) {
    return (
      <div style={{ marginTop: 6, padding: '5px 8px', borderRadius: 4, background: '#fde68a', color: '#000', fontSize: 12, maxWidth: 420 }}>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>Provavel causa:</div>
        {causas.map((c, i) => (
          <div key={i} style={{ marginTop: i > 0 ? 3 : 0 }}>• {c}</div>
        ))}
        <div style={{ marginTop: 4, fontWeight: 600 }}>Corrija no cadastro do produto e clique Emitir.</div>
      </div>
    );
  }
  // Recusa da SEFAZ sem nada achado: diz o que ja foi conferido, pra o vazio nao se ler
  // como "nao sei". O 217 fica de fora — nao e recusa (DA-460).
  const houveRecusa = !!row.codigo_status && row.codigo_status !== '217' && row.categoria !== 'erro_comunicacao';
  if (houveRecusa && row.causas_conferidas) {
    return (
      <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text-muted)' }}>
        Codigo de barras e NCM dos produtos conferidos: estao certos. A causa e outra.
      </div>
    );
  }
  return null;
}

const CATEGORIA_LABEL: Record<string, { label: string; cor: string; fundo: string }> = {
  rejeitada: { label: 'Rejeitada', cor: '#b91c1c', fundo: 'rgba(185,28,28,0.12)' },
  erro_comunicacao: { label: 'Erro comunicacao', cor: '#92400e', fundo: 'rgba(146,64,14,0.12)' },
  venda_sem_nf: { label: 'Venda sem NF', cor: '#7c2d12', fundo: 'rgba(124,45,18,0.15)' },
};

export function ProblemasNotasLista() {
  const toast = useToast();
  const dialog = useDialog();
  const [lista, setLista] = useState<ProblemaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [emitindo, setEmitindo] = useState<number | null>(null);
  const [consultando, setConsultando] = useState<number | null>(null);
  const [ignorando, setIgnorando] = useState<number | null>(null);
  const [cancelando, setCancelando] = useState<number | null>(null);
  const [detalhesRow, setDetalhesRow] = useState<ProblemaRow | null>(null);
  const [comprovanteInfo, setComprovanteInfo] = useState<ComprovanteVendaInfo | null>(null);
  // Tick de 30s pra reavaliar o cooldown do botao Emitir sem depender de recarregar.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<ProblemaRow[]>('/api/notas-fiscais/com-problemas');
      setLista(data);
    } catch (e) {
      toast.erro((e as Error).message);
      setLista([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void carregar(); }, [carregar]);

  async function emitir(linha: ProblemaRow, semDestinatario = false) {
    const tipo = linha.tipo_documento_esperado;
    if (!tipo) {
      toast.erro('Orcamento sem tipo de documento definido');
      return;
    }
    // O rotulo "Consumidor nao Identificado" diz COMO a nota sai, nao que o clique emite —
    // a confirmacao nomeia o efeito irreversivel (DA-434).
    if (semDestinatario) {
      const ok = await dialog.confirmar(
        `Emitir NFC-e sem identificar o consumidor?\n\nA nota sai sem o CPF/CNPJ de ${linha.cliente_nome ?? 'cliente'}. ` +
        `A venda continua no nome do cliente no sistema.`,
        { titulo: 'Consumidor nao Identificado', textoConfirmar: 'Emitir sem identificar' },
      );
      if (!ok) return;
    }
    setEmitindo(linha.orcamento_id);
    try {
      const data = await api.post<{ numero?: number; status?: string; cStat?: string; xMotivo?: string }>(
        `/api/${tipo}/emitir/${linha.orcamento_id}`,
        semDestinatario ? { sem_destinatario: true } : undefined,
      );
      if (data.status === 'autorizada') {
        toast.sucesso(`${tipo === 'nfce' ? 'NFC-e' : 'NF-e'} #${data.numero} autorizada`);
      } else {
        toast.erro(`${data.cStat ?? ''} ${data.xMotivo ?? 'Falha na emissao'}`);
      }
      void carregar();
    } catch (e: any) {
      toast.erro(e?.message || 'Erro ao emitir');
    } finally {
      setEmitindo(null);
    }
  }

  async function cancelarVendaForcado(linha: ProblemaRow) {
    if (!linha.venda_id) return;
    const motivo = window.prompt(
      `Cancelar a venda ${linha.venda_numero}? (estorna estoque, apaga pagamentos, devolve orcamento ao caixa)\n\n` +
      `Informe o motivo (min. 3 caracteres):`,
      'Venda-teste / fora-do-dia',
    );
    if (!motivo || motivo.trim().length < 3) return;
    setCancelando(linha.venda_id);
    try {
      // Venda paga no cartao estorna R$ no MP → o backend exige senha gerencial (403, DA-251).
      // Tela admin de excecao: pedimos a senha por prompt e reenviamos.
      const enviar = (senha?: string) =>
        api.post(`/api/caixa/cancelar-venda/${linha.venda_id}`, {
          motivo: motivo.trim(), forcar: true, ...(senha ? { senha_gerencial: senha } : {}),
        });
      try {
        const r = await enviar();
        toast.sucesso((r as any)?.message || `Venda ${linha.venda_numero} cancelada — estoque estornado`);
      } catch (e1: any) {
        if (e1?.status === 403 && e1?.corpo?.requer_senha_gerencial) {
          const senha = window.prompt('Esta venda foi paga no cartao (Mercado Pago). O cancelamento estorna o valor no cartao do cliente.\n\nInforme a senha gerencial para autorizar:');
          if (!senha) { setCancelando(null); return; }
          const r = await enviar(senha);
          toast.sucesso((r as any)?.message || `Venda ${linha.venda_numero} cancelada — estoque estornado`);
        } else {
          throw e1;
        }
      }
      void carregar();
    } catch (e: any) {
      toast.erro(e?.message || 'Erro ao cancelar');
    } finally {
      setCancelando(null);
    }
  }

  async function ignorarPendencia(linha: ProblemaRow) {
    if (!linha.venda_id) return;
    const confirma = window.confirm(
      `Ignorar a pendencia de NF da venda ${linha.venda_numero}?\n\n` +
      `A venda continua finalizada, mas some da aba "Com problemas" e do banner.\n` +
      `Use para vendas-teste do cutover ou casos onde voce decidiu nao emitir NF.\n` +
      `(Reversivel via SQL se precisar.)`
    );
    if (!confirma) return;
    setIgnorando(linha.venda_id);
    try {
      await api.post(`/api/notas-fiscais/vendas/${linha.venda_id}/ignorar-pendencia-nf`, {});
      toast.sucesso(`Venda ${linha.venda_numero} removida da lista de pendencias`);
      void carregar();
    } catch (e: any) {
      toast.erro(e?.message || 'Erro ao ignorar');
    } finally {
      setIgnorando(null);
    }
  }

  async function consultarSefaz(linha: ProblemaRow) {
    if (!linha.nota_id || !linha.tipo) return;
    setConsultando(linha.nota_id);
    try {
      const data = await api.post<{ cStat?: string; xMotivo?: string; status_local?: string; status_sefaz?: string }>(
        `/api/${linha.tipo}/${linha.nota_id}/consultar-sefaz`,
      );
      const msg = `SEFAZ: ${data.cStat ?? '?'} ${data.xMotivo ?? ''}`;
      if (data.status_sefaz === 'autorizada' || data.status_sefaz === 'cancelada') {
        toast.sucesso(msg);
      } else {
        toast.erro(msg);
      }
      void carregar();
    } catch (e: any) {
      toast.erro(e?.message || 'Erro ao consultar SEFAZ');
    } finally {
      setConsultando(null);
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
        <Loader2 size={20} className="animate-spin" style={{ display: 'inline-block' }} />
      </div>
    );
  }

  if (lista.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
        <div style={{ fontSize: 14, marginBottom: 4 }}>Nenhuma nota com problema nos ultimos 30 dias.</div>
        <div style={{ fontSize: 12 }}>Vendas finalizadas sem NF emitida, NFs rejeitadas e erros de comunicacao aparecem aqui.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-muted)' }}>
        {lista.length} {lista.length === 1 ? 'pendencia' : 'pendencias'} • janela 30 dias • cron reconcilia 'erro_comunicacao' a cada 5min
        <button
          type="button"
          onClick={() => void carregar()}
          style={{ marginLeft: 12, background: 'transparent', border: 0, color: 'var(--primary)', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <RefreshCw size={12} /> Atualizar
        </button>
      </div>

      {/* overflow-x AUTO, nunca hidden: a coluna Acoes tem ate 5 botoes e o `hidden`
          arredonda o canto MAS clipa na horizontal — o que passa da largura simplesmente
          nao existe, sem barra pra rolar ate la. So aparece em tela estreita (tablet). */}
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--table-header-bg)', textAlign: 'left' }}>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Categoria</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Origem</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Cliente</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Vendedor</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Produto(s)</th>
              <th style={{ padding: '8px 10px', fontWeight: 600, textAlign: 'right' }}>Valor</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Motivo</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Quando</th>
              <th style={{ padding: '8px 10px', fontWeight: 600, textAlign: 'right' }}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((row, idx) => {
              const cat = CATEGORIA_LABEL[row.categoria]!;
              const origem = row.tipo
                ? `${row.tipo === 'nfce' ? 'NFC-e' : 'NF-e'} #${row.numero ?? '?'} / serie ${row.serie ?? '?'}`
                : `Venda ${row.venda_numero ?? '?'} (ORC ${row.orcamento_numero ?? '?'})`;
              const podeEmitir = !!row.tipo_documento_esperado;
              const podeSemIdentificar = row.tipo_documento_esperado === 'nfce'
                && row.cliente_id !== null && row.cliente_id !== 1;
              const podeConsultar = row.nota_id !== null && row.chave !== null && row.tipo !== null;
              const podeIgnorar = row.categoria === 'venda_sem_nf' && row.venda_id !== null;
              const podeCancelar = row.categoria === 'venda_sem_nf' && row.venda_id !== null;
              // Cooldown: erro_comunicacao recente (<10min) esta reconciliando sozinho.
              const emCooldown = row.categoria === 'erro_comunicacao'
                && minutosDesde(row.ocorrido_em) < COOLDOWN_EMISSAO_MIN;
              return (
                <tr key={`${row.categoria}-${row.nota_id ?? row.venda_id ?? idx}`} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{
                      background: cat.fundo,
                      color: cat.cor,
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}>
                      <AlertTriangle size={11} /> {cat.label}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>{origem}</td>
                  <td style={{ padding: '8px 10px' }}>{row.cliente_nome ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}>{row.vendedor_nome ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 12, maxWidth: 240 }} title={row.produtos ?? undefined}>
                    {row.produtos ?? '—'}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>
                    {row.valor_total ? formatMoeda(row.valor_total) : '—'}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 12 }}>
                    {row.motivo ?? '—'}
                    {/* [DA-460] O 217 da SEFAZ ("NF nao consta") LE como veredito e nao e:
                        a autorizacao pode estar em processamento e voltar 100 no ciclo
                        seguinte — foi o que aconteceu com a NFC-e 1427 em 06/08/2026, e a
                        leitura errada desse texto e que produziu a 2a nota da mesma venda.
                        A ressalva mora colada no motivo, que e onde a conclusao e tirada. */}
                    {row.codigo_status === '217' && (
                      <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text-muted)' }}>
                        Ainda nao e veredito — a SEFAZ pode estar processando. O sistema segue
                        consultando a cada 5 min; nao emita outra nota por causa deste texto.
                      </div>
                    )}
                    <ProvavelCausa row={row} />
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {formatDataHoraSP(row.ocorrido_em)}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      onClick={() => setDetalhesRow(row)}
                      title="Ver detalhes"
                      style={{
                        background: 'transparent', border: 0, padding: '3px 6px', borderRadius: 4,
                        cursor: 'pointer', color: 'var(--text-muted)', display: 'inline-flex',
                        alignItems: 'center', marginRight: 4,
                      }}
                    >
                      <Info size={14} />
                    </button>
                    {podeEmitir && emCooldown && (
                      <button
                        type="button"
                        disabled
                        title="A SEFAZ nao respondeu na hora e o sistema esta reconciliando esta venda sozinho (a nota sai em ate 5 min). O botao Emitir volta em ate 10 min pra evitar nota duplicada."
                        style={{
                          background: 'var(--table-header-bg)', color: 'var(--text-muted)',
                          border: '1px solid var(--border)', padding: '4px 10px', borderRadius: 4,
                          fontSize: 12, fontWeight: 600, cursor: 'not-allowed', marginRight: 4,
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <Loader2 size={12} className="animate-spin" />
                        SEFAZ instável — reconciliando…
                      </button>
                    )}
                    {podeEmitir && !emCooldown && (
                      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'stretch', gap: 4, verticalAlign: 'middle', marginRight: 4 }}>
                        <button
                          type="button"
                          onClick={() => void emitir(row)}
                          disabled={emitindo === row.orcamento_id}
                          title={row.tipo_documento_esperado === 'nfce' ? 'Emitir NFC-e' : 'Emitir NF-e'}
                          style={{
                            // Mesma cor do Salvar (btn-primario): o lilas que a NF-e usava lia como
                            // botao desabilitado. NFC-e x NF-e segue separado pelo icone.
                            background: 'var(--primary)',
                            color: '#fff', border: 0, padding: '4px 10px', borderRadius: 4,
                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                            opacity: emitindo === row.orcamento_id ? 0.5 : 1,
                          }}
                        >
                          {emitindo === row.orcamento_id
                            ? <Loader2 size={12} className="animate-spin" />
                            : (row.tipo_documento_esperado === 'nfce' ? <Receipt size={12} /> : <FileText size={12} />)}
                          Emitir
                        </button>
                        {/* NFC-e sem <dest> mesmo com cliente identificado (ex.: CNPJ suspenso,
                            cStat 208). So NFC-e — NF-e mod 55 exige destinatario — e so com
                            cliente de cadastro: venda de consumidor ja sai assim pelo Emitir. */}
                        {podeSemIdentificar && (
                          <button
                            type="button"
                            onClick={() => void emitir(row, true)}
                            disabled={emitindo === row.orcamento_id}
                            title="Emitir a NFC-e sem o CPF/CNPJ do cliente (Consumidor nao identificado)"
                            style={{
                              background: 'var(--primary)',
                              color: '#fff', border: 0, padding: '4px 10px', borderRadius: 4,
                              fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                              opacity: emitindo === row.orcamento_id ? 0.5 : 1,
                            }}
                          >
                            <Receipt size={12} /> Consumidor não Identificado
                          </button>
                        )}
                      </span>
                    )}
                    {/* Comprovante nao-fiscal: oferecido em TODA linha desta aba — em qualquer
                        uma delas o cliente pagou e nao tem nota na mao. Laranja SOLIDO + preto:
                        na casa o laranja significa pendencia (nao erro, que e o vermelho do
                        Cancelar) e e a unica cor livre nesta faixa; contornado, ele lia como
                        texto desabilitado e o Ruan nao viu que a ferramenta existia. */}
                    <button
                      type="button"
                      onClick={() => setComprovanteInfo({
                        orcamentoId: row.orcamento_id,
                        clienteNome: row.cliente_nome,
                        valorTotal: row.valor_total,
                        vendaNumero: row.venda_numero,
                        orcamentoNumero: row.orcamento_numero,
                      })}
                      title="Gerar o comprovante nao fiscal desta venda pro cliente levar enquanto a NF nao sai"
                      style={{
                        background: '#f97316', color: '#000',
                        border: 0, padding: '4px 10px', borderRadius: 4,
                        fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 4,
                        display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                      }}
                    >
                      <Printer size={12} /> Comprovante não fiscal
                    </button>
                    {podeConsultar && (
                      <button
                        type="button"
                        onClick={() => void consultarSefaz(row)}
                        disabled={consultando === row.nota_id}
                        title="Consultar status real na SEFAZ"
                        style={{
                          background: 'transparent', color: 'var(--primary)',
                          border: '1px solid var(--primary)', padding: '3px 8px', borderRadius: 4,
                          fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          opacity: consultando === row.nota_id ? 0.5 : 1,
                          marginRight: 4,
                        }}
                      >
                        {consultando === row.nota_id
                          ? <Loader2 size={12} className="animate-spin" />
                          : <Shield size={12} />}
                        SEFAZ
                      </button>
                    )}
                    {podeCancelar && (
                      <button
                        type="button"
                        onClick={() => void cancelarVendaForcado(row)}
                        disabled={cancelando === row.venda_id}
                        title="Cancelar venda (estorna estoque + apaga pagamentos + remove de comissao/faturamento)"
                        style={{
                          background: '#dc2626', color: '#fff',
                          border: 0, padding: '4px 10px', borderRadius: 4,
                          fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          opacity: cancelando === row.venda_id ? 0.5 : 1,
                          marginRight: 4,
                        }}
                      >
                        {cancelando === row.venda_id
                          ? <Loader2 size={12} className="animate-spin" />
                          : <Ban size={12} />}
                        Cancelar
                      </button>
                    )}
                    {podeIgnorar && (
                      <button
                        type="button"
                        onClick={() => void ignorarPendencia(row)}
                        disabled={ignorando === row.venda_id}
                        title="Marcar como resolvido sem emitir NF (some da lista)"
                        style={{
                          background: 'transparent', color: 'var(--text-muted)',
                          border: '1px solid var(--border)', padding: '3px 8px', borderRadius: 4,
                          fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          opacity: ignorando === row.venda_id ? 0.5 : 1,
                        }}
                      >
                        {ignorando === row.venda_id
                          ? <Loader2 size={12} className="animate-spin" />
                          : <EyeOff size={12} />}
                        Ignorar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {detalhesRow && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}
        >
          <div style={{
            background: 'var(--modal-bg, var(--panel-bg, #fff))',
            border: '1px solid var(--border)',
            borderRadius: 8, padding: 0, width: 480, maxWidth: '95vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div className="modal-header" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', borderBottom: '1px solid var(--border)',
              cursor: 'move',
            }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>
                Detalhes da pendencia — {detalhesRow.orcamento_numero ? `ORC ${detalhesRow.orcamento_numero}` : detalhesRow.venda_numero ?? '?'}
              </span>
              <button
                type="button"
                onClick={() => setDetalhesRow(null)}
                className="btn-fechar-modal"
                style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 2 }}
              >
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: 13 }}>
              {[
                ['Categoria', CATEGORIA_LABEL[detalhesRow.categoria]?.label ?? detalhesRow.categoria],
                ['Origem', detalhesRow.tipo
                  ? `${detalhesRow.tipo === 'nfce' ? 'NFC-e' : 'NF-e'} #${detalhesRow.numero ?? '?'} / serie ${detalhesRow.serie ?? '?'}`
                  : `Venda ${detalhesRow.venda_numero ?? '?'} (ORC ${detalhesRow.orcamento_numero ?? '?'})`],
                ['Cliente', detalhesRow.cliente_nome ?? '—'],
                ['Vendedor', detalhesRow.vendedor_nome ?? '—'],
                ['Valor', detalhesRow.valor_total ? formatMoeda(detalhesRow.valor_total) : '—'],
                ['Quando', formatDataHoraSP(detalhesRow.ocorrido_em)],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontWeight: 600 }}>{value}</div>
                </div>
              ))}
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Produto(s)</div>
                <div style={{ fontWeight: 600, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{detalhesRow.produtos ?? '—'}</div>
              </div>
              {detalhesRow.motivo && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Motivo</div>
                  <div style={{ fontWeight: 600 }}>{detalhesRow.motivo}</div>
                  <ProvavelCausa row={detalhesRow} />
                </div>
              )}
              {detalhesRow.chave && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Chave</div>
                  <div style={{ fontSize: 11, wordBreak: 'break-all', fontFamily: 'monospace' }}>{detalhesRow.chave}</div>
                </div>
              )}
            </div>
            <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => setDetalhesRow(null)}
                style={{
                  background: 'transparent', border: '1px solid var(--border)', padding: '5px 16px',
                  borderRadius: 4, fontSize: 12, cursor: 'pointer', color: 'var(--text-muted)',
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      {comprovanteInfo && (
        <ModalComprovanteVenda info={comprovanteInfo} onFechar={() => setComprovanteInfo(null)} />
      )}
    </div>
  );
}
